import type { ReactNode } from 'react'

import type {
  ObservabilityAnalysisFinding,
  ObservabilityAnalysisRecommendation,
  ObservabilityAnalysisResponse,
  ObservabilityAnalysisRisk,
} from '../services/traceService'

type DebugAnalysisResultPanelProps = {
  result: ObservabilityAnalysisResponse['data']
}

function toneForSeverity(severity: ObservabilityAnalysisFinding['severity'] | ObservabilityAnalysisRisk['severity']) {
  switch (severity) {
    case 'critical':
      return { border: 'rgba(239,68,68,0.40)', background: 'rgba(239,68,68,0.12)', color: '#fecaca' }
    case 'high':
      return { border: 'rgba(251,146,60,0.35)', background: 'rgba(251,146,60,0.12)', color: '#fed7aa' }
    case 'medium':
      return { border: 'rgba(250,204,21,0.28)', background: 'rgba(250,204,21,0.10)', color: '#fef08a' }
    case 'low':
    default:
      return { border: 'rgba(34,197,94,0.30)', background: 'rgba(34,197,94,0.10)', color: '#bbf7d0' }
  }
}

function toneForPriority(priority: ObservabilityAnalysisRecommendation['priority']) {
  switch (priority) {
    case 'p0':
      return { border: 'rgba(239,68,68,0.40)', background: 'rgba(239,68,68,0.12)', color: '#fecaca' }
    case 'p1':
      return { border: 'rgba(251,146,60,0.35)', background: 'rgba(251,146,60,0.12)', color: '#fed7aa' }
    case 'p2':
      return { border: 'rgba(59,130,246,0.30)', background: 'rgba(59,130,246,0.10)', color: '#bfdbfe' }
    case 'p3':
    default:
      return { border: 'rgba(148,163,184,0.22)', background: 'rgba(148,163,184,0.10)', color: '#e2e8f0' }
  }
}

function badgeStyle(entry: { border: string; background: string; color: string }) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3px 8px',
    borderRadius: 999,
    border: `1px solid ${entry.border}`,
    background: entry.background,
    color: entry.color,
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
  }
}

function sectionCard(title: string, children: ReactNode, extra?: ReactNode) {
  return (
    <div style={{ border: '1px solid rgba(255,255,255,0.10)', borderRadius: 14, background: 'rgba(255,255,255,0.02)', padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 900 }}>{title}</div>
        {extra}
      </div>
      {children}
    </div>
  )
}

export default function DebugAnalysisResultPanel({ result }: DebugAnalysisResultPanelProps) {
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr', gap: 10 }}>
        <div style={{ padding: 14, borderRadius: 14, border: '1px solid rgba(59,130,246,0.22)', background: 'rgba(59,130,246,0.10)' }}>
          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(226,232,240,0.55)' }}>Resumen</div>
          <div style={{ marginTop: 8, fontSize: 14, fontWeight: 800, lineHeight: 1.5 }}>{result.summary}</div>
        </div>
        <div style={{ padding: 14, borderRadius: 14, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.02)' }}>
          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(226,232,240,0.55)' }}>Proveedor</div>
          <div style={{ marginTop: 8, fontSize: 14, fontWeight: 800 }}>{result.provider}</div>
          <div style={{ marginTop: 4, fontSize: 12, color: 'rgba(226,232,240,0.70)' }}>{result.model}</div>
        </div>
        <div style={{ padding: 14, borderRadius: 14, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.02)' }}>
          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(226,232,240,0.55)' }}>Confianza</div>
          <div style={{ marginTop: 8, fontSize: 20, fontWeight: 900 }}>{Math.round(result.confidence * 100)}%</div>
        </div>
        <div style={{ padding: 14, borderRadius: 14, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.02)' }}>
          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(226,232,240,0.55)' }}>Latencia / Tokens</div>
          <div style={{ marginTop: 8, fontSize: 14, fontWeight: 800 }}>{Math.round(result.latencyMs)}ms</div>
          <div style={{ marginTop: 4, fontSize: 12, color: 'rgba(226,232,240,0.70)' }}>{result.tokensUsed} tokens</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12, marginTop: 12 }}>
        {sectionCard(
          'Hallazgos',
          result.findings.length === 0 ? (
            <div style={{ color: 'rgba(226,232,240,0.55)' }}>Sin hallazgos.</div>
          ) : (
            <div style={{ display: 'grid', gap: 10 }}>
              {result.findings.map((finding, index) => {
                const severityTone = toneForSeverity(finding.severity)
                return (
                  <div key={`${index}-${finding.title}`} style={{ border: `1px solid ${severityTone.border}`, borderRadius: 12, background: severityTone.background, padding: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                      <div style={{ fontWeight: 800, lineHeight: 1.4 }}>{finding.title}</div>
                      <span style={badgeStyle(severityTone)}>{finding.severity}</span>
                    </div>
                    <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
                      <div style={{ fontSize: 11, color: 'rgba(226,232,240,0.66)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Evidencia</div>
                      <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 4 }}>
                        {finding.evidence.map((item, evidenceIndex) => <li key={`${index}-${evidenceIndex}`}>{item}</li>)}
                      </ul>
                      {finding.probableCause ? (
                        <div style={{ marginTop: 4, fontSize: 12, color: 'rgba(226,232,240,0.78)' }}>
                          <b>Causa probable:</b> {finding.probableCause}
                        </div>
                      ) : null}
                    </div>
                  </div>
                )
              })}
            </div>
          ),
        )}

        {sectionCard(
          'Recomendaciones',
          result.recommendations.length === 0 ? (
            <div style={{ color: 'rgba(226,232,240,0.55)' }}>Sin recomendaciones.</div>
          ) : (
            <div style={{ display: 'grid', gap: 10 }}>
              {result.recommendations.map((recommendation, index) => {
                const priorityTone = toneForPriority(recommendation.priority)
                return (
                  <div key={`${index}-${recommendation.action}`} style={{ border: `1px solid ${priorityTone.border}`, borderRadius: 12, background: priorityTone.background, padding: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                      <div style={{ fontWeight: 800, lineHeight: 1.4 }}>{recommendation.action}</div>
                      <span style={badgeStyle(priorityTone)}>{recommendation.priority}</span>
                    </div>
                    <div style={{ marginTop: 8, fontSize: 12, color: 'rgba(226,232,240,0.78)' }}>{recommendation.rationale}</div>
                  </div>
                )
              })}
            </div>
          ),
        )}

        {sectionCard(
          'Riesgos',
          result.risks.length === 0 ? (
            <div style={{ color: 'rgba(226,232,240,0.55)' }}>Sin riesgos.</div>
          ) : (
            <div style={{ display: 'grid', gap: 10 }}>
              {result.risks.map((risk, index) => {
                const severityTone = toneForSeverity(risk.severity)
                return (
                  <div key={`${index}-${risk.description}`} style={{ border: `1px solid ${severityTone.border}`, borderRadius: 12, background: severityTone.background, padding: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                      <div style={{ fontWeight: 800, lineHeight: 1.4 }}>{risk.description}</div>
                      <span style={badgeStyle(severityTone)}>{risk.severity}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          ),
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, marginTop: 12 }}>
        {sectionCard(
          'Areas sospechosas',
          result.suspectAreas.length === 0 ? (
            <div style={{ color: 'rgba(226,232,240,0.55)' }}>Sin modulos sospechosos detectados.</div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {result.suspectAreas.map((item) => (
                <span
                  key={item}
                  style={{
                    display: 'inline-flex',
                    padding: '5px 8px',
                    borderRadius: 999,
                    border: '1px solid rgba(148,163,184,0.24)',
                    background: 'rgba(148,163,184,0.08)',
                    color: '#e2e8f0',
                    fontSize: 11,
                  }}
                >
                  {item}
                </span>
              ))}
            </div>
          ),
        )}

        {sectionCard(
          'Pasos de repro',
          result.reproSteps.length === 0 ? (
            <div style={{ color: 'rgba(226,232,240,0.55)' }}>Sin pasos de repro generados.</div>
          ) : (
            <ol style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 6 }}>
              {result.reproSteps.map((step, index) => (
                <li key={`${index}-${step}`} style={{ lineHeight: 1.5 }}>{step}</li>
              ))}
            </ol>
          ),
        )}

        {sectionCard(
          'Pruebas a agregar',
          result.testsToAdd.length === 0 ? (
            <div style={{ color: 'rgba(226,232,240,0.55)' }}>Sin pruebas sugeridas.</div>
          ) : (
            <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 6 }}>
              {result.testsToAdd.map((test, index) => <li key={`${index}-${test}`}>{test}</li>)}
            </ul>
          ),
        )}
      </div>

      <div style={{ marginTop: 12 }}>
        {sectionCard(
          'Prompt para Codex',
          result.codexInvestigationPrompt ? (
            <pre
              style={{
                margin: 0,
                whiteSpace: 'pre-wrap',
                fontSize: 11,
                lineHeight: 1.55,
                color: '#e2e8f0',
                background: 'rgba(59,130,246,0.06)',
                border: '1px solid rgba(59,130,246,0.16)',
                borderRadius: 12,
                padding: 12,
              }}
            >
              {result.codexInvestigationPrompt}
            </pre>
          ) : (
            <div style={{ color: 'rgba(226,232,240,0.55)' }}>No se genero un prompt de investigacion para Codex.</div>
          ),
        )}
      </div>

      <details style={{ marginTop: 12 }}>
        <summary style={{ fontSize: 11, color: 'rgba(226,232,240,0.65)', cursor: 'pointer' }}>Ver resultado crudo</summary>
        <pre style={{ marginTop: 8, marginBottom: 0, whiteSpace: 'pre-wrap', fontSize: 11, color: '#a7f3d0', background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.18)', borderRadius: 12, padding: 12 }}>
{JSON.stringify(result, null, 2)}
        </pre>
      </details>
    </>
  )
}
